import { DateRange } from 'components/tables/list-table/filter-pieces/inputs/DateRange';
import { DateRangeFilterForm } from 'components/tables/list-table/ListTableTypes';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useState } from 'react';
import { t } from 'utils/getTranslations';
import { FilterTriggerIndicator } from './FilterTriggerIndicator';
import { FILTER_TRIGGER_CLASSES } from './toolbarStyles';

export interface ChangedAtRange {
    from: string | null;
    to: string | null;
}

/** The state day (YYYY-MM-DD) as a local-midnight date string, so the picker shows the same calendar day. */
function toPickerValue(day: string | null): string | null {
    if (!day) {
        return null;
    }
    const [year, month, date] = day.split('-').map(Number);
    return String(new Date(year, month - 1, date));
}

/** The picker's local Date string back to the calendar day it shows. */
function toDayString(value: number | string | null): string | null {
    if (value === null) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function TimeFilter({
    range,
    onChange,
}: {
    range: ChangedAtRange;
    onChange: (update: Partial<ChangedAtRange>) => void;
}) {
    const [open, setOpen] = useState(false);
    const boundsCount = (range.from ? 1 : 0) + (range.to ? 1 : 0);

    // Reuses the shared list-filter date-range piece: two date fields + the
    // today/yesterday/week/month quick-picks.
    const filterForm: DateRangeFilterForm = {
        type: 'daterange',
        selector: 'changedAt',
        label: t('audit-trail.time-from'),
        labelTo: t('audit-trail.time-to'),
        shouldDisplayQuickPick: true,
        from: toPickerValue(range.from),
        to: toPickerValue(range.to),
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger className={FILTER_TRIGGER_CLASSES}>
                {t('audit-trail.time')}
                <FilterTriggerIndicator count={boundsCount} />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-120 rounded-xl p-3">
                <div className="flex flex-col gap-2.5">
                    <DateRange
                        filter={filterForm}
                        quickFilters={{}}
                        mainFilters={{}}
                        onChange={(value, _groupSelector, setMin) =>
                            onChange(setMin ? { from: toDayString(value) } : { to: toDayString(value) })
                        }
                    />
                    <p className="text-grey-400 new:text-caption text-[11.5px]">{t('audit-trail.time-utc-hint')}</p>
                    <div className="border-grey-100 border-t pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-grey-600 w-full text-[12.5px]"
                            disabled={boundsCount === 0}
                            onClick={() => onChange({ from: null, to: null })}
                        >
                            {t('audit-trail.clear-all')}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
